import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useToasts, type Toast, type ToastTone } from '../stores/toasts';
import styles from './Toast.module.css';

const TONE_CLASS: Readonly<Record<ToastTone, string>> = {
  success: styles.success,
  warning: styles.warning,
  danger: styles.danger,
  info: styles.info,
};

const TONE_ICON = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
} as const;

function ToastItem({ toast }: { toast: Toast }): ReactNode {
  const dismiss = useToasts((state) => state.dismiss);
  const Icon = TONE_ICON[toast.tone];

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.durationMs, dismiss]);

  // The draining bar runs for exactly the toast's lifetime. A measured duration
  // driving an animation, not an appearance choice.
  const timerStyle: CSSProperties = { animationDuration: `${toast.durationMs}ms` };

  return (
    <div className={`${styles.toast} ${TONE_CLASS[toast.tone]} ${styles.hasTimer}`} role="status">
      <span className={styles.icon}>
        <Icon size={16} aria-hidden />
      </span>
      <div className={styles.text}>
        <span className={styles.message}>{toast.message}</span>
        {toast.detail !== undefined && <span className={styles.detail}>{toast.detail}</span>}
      </div>
      {toast.action && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            toast.action?.run();
            // The toast has done its job once its action is taken; leaving it up
            // reads as the click not having worked.
            dismiss(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className={styles.dismiss}
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss"
      >
        <X size={14} aria-hidden />
      </button>
      <span className={styles.timer} style={timerStyle} aria-hidden />
    </div>
  );
}

/**
 * Mounted once by the shell. `aria-live="polite"` so a screen reader is told what
 * happened without being interrupted mid-sentence.
 */
export function ToastStack(): ReactNode {
  const toasts = useToasts((state) => state.toasts);

  return createPortal(
    <div className={styles.stack} aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body,
  );
}
